import { type ReactElement } from "react";
import Markdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

interface MarkdownRendererProps {
  content: string;
}

// XSS defense: rehype-sanitize strips all dangerous HTML (scripts, iframes, event handlers).
// Links forced to target="_blank" with noopener noreferrer.
export function MarkdownRenderer({ content }: MarkdownRendererProps): ReactElement {
  return (
    <div data-testid="markdown-content" className="markdown-content">
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ children, href, ...props }) => (
            <a
              {...props}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn("text-primary underline underline-offset-2 hover:text-primary/80")}
            >
              {children}
            </a>
          ),
          pre: ({ children, ...props }) => (
            <pre
              {...props}
              className={cn("my-2 overflow-x-auto rounded-md bg-muted p-3", "text-xs font-mono")}
            >
              {children}
            </pre>
          ),
          code: ({ children, className, ...props }) => {
            const isBlock = className?.includes("language-");
            if (isBlock) {
              return (
                <code {...props} className={cn(className, "text-xs")}>
                  {children}
                </code>
              );
            }
            return (
              <code {...props} className={cn("rounded bg-muted px-1 py-0.5", "text-xs font-mono")}>
                {children}
              </code>
            );
          },
          ul: ({ children, ...props }) => (
            <ul {...props} className="my-1 ml-4 list-disc space-y-0.5">
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol {...props} className="my-1 ml-4 list-decimal space-y-0.5">
              {children}
            </ol>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              {...props}
              className={cn("my-2 border-l-2 border-muted-foreground/30 pl-3", "italic")}
            >
              {children}
            </blockquote>
          ),
          h1: ({ children, ...props }) => (
            <h1 {...props} className="mt-3 mb-1 text-base font-bold">
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 {...props} className="mt-2 mb-1 text-sm font-bold">
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 {...props} className="mt-2 mb-1 text-sm font-semibold">
              {children}
            </h3>
          ),
          table: ({ children, ...props }) => (
            <div className="my-2 overflow-x-auto">
              <table {...props} className="min-w-full text-xs">
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th {...props} className="border-b border-border px-2 py-1 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td {...props} className="border-b border-border/50 px-2 py-1">
              {children}
            </td>
          ),
          p: ({ children, ...props }) => (
            <p {...props} className="my-1">
              {children}
            </p>
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}
